import React, { PureComponent } from 'react';
import { st, classes } from './CounterBadge.st.css';
import { CaptionWithEllipsis as Caption } from '../Text/Caption';
import { Text } from '../Text';
import { dataHooks } from './constants';
import { WixStyleReactEnvironmentContext } from '../WixStyleReactEnvironmentProvider/context';
import { CounterBadgeProps, CounterBadgeSize } from './CounterBadge.types';

const MAX_NUMBER = 100;

type DefaultProps = Required<
  Pick<CounterBadgeProps, 'skin' | 'size' | 'truncate' | 'showShadow' | 'type'>
>;
type Props = CounterBadgeProps & DefaultProps;

/** CounterBadge */
class CounterBadge extends PureComponent<Props> {
  context!: React.ContextType<typeof WixStyleReactEnvironmentContext>;

  static displayName = 'CounterBadge';
  static defaultProps: DefaultProps = {
    skin: 'general',
    size: 'small',
    showShadow: false,
    truncate: true,
    type: 'solid',
  };

  _renderNumberContent = (n: number, truncate: boolean) =>
    n < MAX_NUMBER || !truncate ? (
      Intl.NumberFormat(this._getLocale()).format(n)
    ) : (
      <div className={classes.numberContent}>
        {MAX_NUMBER - 1}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="5"
          height="5"
          viewBox="0 0 5 5"
        >
          <path
            fill="currentColor"
            d="M3,0 L3,2 L5,2 L5,3 L3,3 L3,5 L2,5 L2,3 L0,3 L0,2 L2,2 L2,0 L3,0 Z"
          />
        </svg>
      </div>
    );

  _renderCounterBadgeContent = (
    size: CounterBadgeSize,
    content: React.ReactNode,
  ) =>
    size === 'small' ? (
      <Caption
        tagName="span"
        caption="c1"
        light
        dataHook={dataHooks.content}
        className={classes.caption}
      >
        {content}
      </Caption>
    ) : (
      <Text
        dataHook={dataHooks.content}
        size="tiny"
        weight="bold"
        light
        className={classes.text}
      >
        {content}
      </Text>
    );

  _getLocale() {
    return this.props.locale || this.context.locale || 'en';
  }

  render() {
    const {
      dataHook,
      size,
      skin,
      type,
      children,
      className,
      showShadow,
      truncate,
      onMouseEnter,
      onMouseLeave,
    } = this.props;
    const custom = isNaN(Number(children));
    const longNumber = !custom && truncate && Number(children) >= MAX_NUMBER;

    const content = custom
      ? children
      : this._renderNumberContent(Number(children), truncate);

    return (
      <div
        className={st(
          classes.root,
          { skin, type, custom, size, longNumber, showShadow },
          className,
        )}
        data-hook={dataHook}
        data-locale={this._getLocale()}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {this._renderCounterBadgeContent(size, content)}
      </div>
    );
  }
}

CounterBadge.contextType = WixStyleReactEnvironmentContext;

export { CounterBadge };
